import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';
import {
    ArrowLeft16Regular,
    Bookmark16Filled,
    Bookmark16Regular,
    Copy16Regular,
    Delete16Regular,
    MoreVertical16Regular,
    Rename16Regular,
    Send16Regular,
    Sparkle16Filled,
    LockClosed16Regular,
    Search16Regular,
    Add16Regular,
    Eye16Regular,
    CheckboxChecked16Regular,
    ArrowDownload16Regular,
    DocumentAdd16Regular,
    Info16Regular,
    Mic20Regular,
    MicOff20Regular,
} from '@fluentui/react-icons';
import { Menu, MenuTrigger, MenuPopover, MenuList, MenuItem, Spinner } from '@fluentui/react-components';
import { ThinkingOrb } from 'thinking-orbs';
import Pageheader from '../../../utils/headerHelment';
import useSpeechDictation, {
    formatSpeechDictationError,
} from '../../../hooks/useSpeechDictation';
import { GetNotes, PostNote } from '../../../redux/dashboardSlice';
import { GetProjectUsers } from '../../../redux/teamSlice';
import {
    createJournalAnalysisChat,
    createJournalAnalysisPrompt,
    deleteJournalAnalysisChat,
    fetchJournalAnalysisChats,
    fetchJournalAnalysisMessages,
    fetchJournalAnalysisPrompts,
    patchJournalAnalysisChat,
    sendJournalAnalysisMessage,
    setActiveChatId,
    clearJournalAnalysisError,
    resetJournalAnalysis,
} from '../../../redux/journalAnalysisSlice';
import { ObservationFormSidebar } from '../../Observations';
import TaskFormSidebar from '../../TaskManagement/TaskFormSidebar';
import { exportNoteAsPdf } from '../exportNote';
import { buildExportPdfFilename } from '../../../utils/exportPdfFilename';
import { getInitials, htmlToPlainText } from '../../../utils/commonUtils';
import { markdownToJournalHtml } from '../../../utils/markdownToJournalHtml';
import { markdownToPdfHtml } from '../../../utils/markdownToPdfHtml';
import { getShortDisplayName } from '../journalUtils';
import ContextModal from './ContextModal';
import RecordingPopover from './RecordingPopover';
import AskAiSavedPromptsModal from '../../LensIntelligence/AskAiSavedPromptsModal';
import { titleFromPromptText } from '../../../utils/askAiChatMeta';
import { SUGGESTED_ANALYSES, formatMessageTime, formatRelativeChatTime, groupChatsByRecency } from './analysisUtils';
import styles from './index.module.css';

const JournalAnalysis = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const user = useSelector(state => state.user?.user);
    const selectedProject = useSelector(state => state.projectData?.selectedProject);
    const projectId = selectedProject?.id || localStorage['project_id'];
    const notesState = useSelector(state => state.dashboard?.notes || []);
    const projectUsersAll = useSelector(state => state.team?.projectUsersAll || []);
    const { chats, activeChatId, messages, savedPrompts, listLoading, messagesLoading, sending, error } = useSelector(
        state => state.journalAnalysis,
    );

    const [search, setSearch] = useState('');
    const [draft, setDraft] = useState('');
    const [contextOpen, setContextOpen] = useState(false);
    const [savedPromptsOpen, setSavedPromptsOpen] = useState(false);
    const [renamingId, setRenamingId] = useState(null);
    const [renameValue, setRenameValue] = useState('');
    const [showObsSidebar, setShowObsSidebar] = useState(false);
    const [showTaskSidebar, setShowTaskSidebar] = useState(false);
    const [sidebarContent, setSidebarContent] = useState('');
    const threadRef = useRef(null);
    const committedDraftRef = useRef('');
    const draftRef = useRef('');

    const activeChat = useMemo(() => chats.find(c => c.id === activeChatId) || null, [chats, activeChatId]);
    const hasContext = (activeChat?.context_note_ids || []).length > 0;
    const contextSectionNames = activeChat?.context_section_names || [];
    const visibleSectionChips = contextSectionNames.slice(0, 3);
    const hiddenSectionCount = Math.max(0, contextSectionNames.length - visibleSectionChips.length);
    const contextEntryCount = activeChat?.context_entry_count || 0;
    const contextSectionCount = activeChat?.context_section_count || contextSectionNames.length;

    const userFullName = user?.full_name || '';
    const userDisplayName = getShortDisplayName(userFullName) || userFullName || 'You';
    const userInitials = getInitials(userFullName) || 'Y';

    useEffect(() => {
        draftRef.current = draft;
    }, [draft]);

    const appendDictationText = useCallback((base, text) => {
        const normalized = (text || '').trim();
        if (!normalized) return base;
        const needsSpace = (base || '').trim().length > 0 && !/\s$/.test(base || '');
        return `${base || ''}${needsSpace ? ' ' : ''}${normalized}`;
    }, []);

    const handleDictationFinal = useCallback(
        text => {
            const next = appendDictationText(committedDraftRef.current, text);
            committedDraftRef.current = next;
            setDraft(next);
        },
        [appendDictationText],
    );

    const handleDictationInterim = useCallback(
        text => {
            setDraft(appendDictationText(committedDraftRef.current, text));
        },
        [appendDictationText],
    );

    const handleDictationError = useCallback(message => {
        toast.error(message || 'Voice dictation failed.');
    }, []);

    const {
        isListening,
        start: startDictation,
        stop: stopDictation,
    } = useSpeechDictation({
        onFinalTranscript: handleDictationFinal,
        onInterimChange: handleDictationInterim,
        onError: handleDictationError,
    });

    const handleToggleMic = async () => {
        if (isListening) {
            await stopDictation();
            setDraft(committedDraftRef.current || draftRef.current);
            return;
        }
        committedDraftRef.current = draftRef.current;
        try {
            await startDictation();
        } catch (e) {
            toast.error(
                formatSpeechDictationError(e?.message || 'Could not start voice dictation.'),
            );
        }
    };

    useEffect(() => {
        setDraft('');
        setSearch('');
        committedDraftRef.current = '';
        dispatch(resetJournalAnalysis());
        dispatch(GetNotes());
        dispatch(GetProjectUsers());
        dispatch(fetchJournalAnalysisChats({ reset: true }));
        dispatch(fetchJournalAnalysisPrompts());
        return () => {
            dispatch(clearJournalAnalysisError());
        };
    }, [dispatch, projectId]);

    useEffect(() => {
        if (!activeChatId) return;
        // Only load messages for chats that belong to the current project's list.
        if (!chats.some(c => c.id === activeChatId)) return;
        dispatch(fetchJournalAnalysisMessages(activeChatId));
    }, [activeChatId, chats, dispatch]);

    useEffect(() => {
        if (threadRef.current) {
            threadRef.current.scrollTop = threadRef.current.scrollHeight;
        }
    }, [messages, sending]);

    const filteredChats = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return chats;
        return chats.filter(c => (c.title || 'Untitled analysis').toLowerCase().includes(q));
    }, [chats, search]);

    const grouped = useMemo(() => groupChatsByRecency(filteredChats), [filteredChats]);

    const isDraftSavedPrompt = useMemo(() => {
        const text = draft.trim();
        if (!text) return false;
        return (savedPrompts || []).some(p => (p.prompt || '').trim() === text);
    }, [draft, savedPrompts]);

    const ensureChat = async () => {
        if (activeChatId) return activeChatId;
        const chat = await dispatch(createJournalAnalysisChat({})).unwrap();
        return chat.id;
    };

    const handleNewAnalysis = async () => {
        try {
            await dispatch(createJournalAnalysisChat({})).unwrap();
            setDraft('');
            setContextOpen(true);
        } catch (e) {
            toast.error(typeof e === 'string' ? e : 'Could not create analysis');
        }
    };

    const handleSelectChat = chatId => {
        dispatch(setActiveChatId(chatId));
        setDraft('');
    };

    const handleConfirmContext = async noteIds => {
        try {
            let chatId = activeChatId;
            if (!chatId) {
                const chat = await dispatch(createJournalAnalysisChat({ context_note_ids: noteIds })).unwrap();
                chatId = chat.id;
            } else {
                await dispatch(patchJournalAnalysisChat({ chatId, context_note_ids: noteIds })).unwrap();
            }
            setContextOpen(false);
            toast.success('Context updated');
        } catch (e) {
            toast.error(typeof e === 'string' ? e : 'Could not update context');
        }
    };

    const handleSend = async text => {
        const message = (text ?? draft).trim();
        if (!message || sending) return;
        if (!hasContext) {
            setContextOpen(true);
            toast.info('Select journal context first');
            return;
        }
        if (isListening) {
            await stopDictation();
        }
        try {
            const chatId = await ensureChat();
            const tempUserId = `temp-user-${Date.now()}`;
            setDraft('');
            committedDraftRef.current = '';
            await dispatch(sendJournalAnalysisMessage({ chatId, message, tempUserId })).unwrap();
        } catch (e) {
            toast.error(typeof e === 'string' ? e : 'Failed to send message');
        }
    };

    const handleSavePrompt = async () => {
        const prompt = draft.trim();
        if (!prompt) {
            toast.info('Type a prompt before saving');
            return;
        }
        if (isDraftSavedPrompt) {
            toast.info('Prompt already saved');
            return;
        }
        try {
            await dispatch(
                createJournalAnalysisPrompt({
                    title: titleFromPromptText(prompt),
                    prompt,
                }),
            ).unwrap();
            toast.success('Prompt saved');
        } catch (e) {
            toast.error(typeof e === 'string' ? e : 'Could not save prompt');
        }
    };

    const handleRename = async chatId => {
        const title = renameValue.trim();
        if (!title) {
            setRenamingId(null);
            return;
        }
        try {
            await dispatch(patchJournalAnalysisChat({ chatId, title })).unwrap();
            setRenamingId(null);
        } catch (e) {
            toast.error(typeof e === 'string' ? e : 'Could not rename');
        }
    };

    const handleDeleteChat = async chatId => {
        try {
            await dispatch(deleteJournalAnalysisChat(chatId)).unwrap();
        } catch (e) {
            toast.error(typeof e === 'string' ? e : 'Could not delete');
        }
    };

    const handleCopy = async content => {
        try {
            await navigator.clipboard.writeText(content || '');
            toast.success('Copied');
        } catch {
            toast.error('Could not copy');
        }
    };

    const handleSaveAsJournal = async content => {
        const section = notesState?.[0];
        if (!section?.id) {
            toast.error('No journal section available');
            return;
        }
        const title = activeChat?.title || 'Journal Analysis';
        const bodyHtml = markdownToJournalHtml(content || '');
        try {
            await dispatch(
                PostNote({
                    notepad: section.id,
                    notes: bodyHtml || `<p></p>`,
                }),
            ).unwrap();
            toast.success('Saved as journal entry');
        } catch {
            toast.error('Could not save journal entry');
        }
    };

    const handleExportPdf = async content => {
        try {
            const bodyHtml = await markdownToPdfHtml(content || '');
            await exportNoteAsPdf({
                bodyHtml: bodyHtml || '<p></p>',
                includeTitle: false,
                filename: buildExportPdfFilename({
                    project: selectedProject,
                    label: 'Journal Analysis',
                }),
            });
        } catch {
            toast.error('Could not export PDF');
        }
    };

    const renderChatGroup = (label, items) => {
        if (!items?.length) return null;
        return (
            <div key={label}>
                <div className={styles.groupLabel}>{label}</div>
                {items.map(chat => {
                    const isActive = chat.id === activeChatId;
                    const title = chat.title || 'Untitled analysis';
                    return (
                        <div
                            key={chat.id}
                            className={`${styles.chatItem} ${isActive ? styles.chatItemActive : ''}`}
                            onClick={() => handleSelectChat(chat.id)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => {
                                if (e.key === 'Enter') handleSelectChat(chat.id);
                            }}>
                            <div className={styles.chatItemMain}>
                                {renamingId === chat.id ? (
                                    <input
                                        value={renameValue}
                                        autoFocus
                                        onClick={e => e.stopPropagation()}
                                        onChange={e => setRenameValue(e.target.value)}
                                        onBlur={() => handleRename(chat.id)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') handleRename(chat.id);
                                            if (e.key === 'Escape') setRenamingId(null);
                                        }}
                                    />
                                ) : (
                                    <div className={styles.chatItemTitle}>{title}</div>
                                )}
                                <div className={styles.chatItemMeta}>
                                    {chat.context_entry_count || 0} entries · {chat.context_section_count || 0} sections · {formatRelativeChatTime(chat.updated_at)}
                                </div>
                            </div>
                            <div className={styles.chatItemMenu} onClick={e => e.stopPropagation()}>
                                <Menu>
                                    <MenuTrigger disableButtonEnhancement>
                                        <button type="button" className={styles.iconAction} aria-label="Chat actions">
                                            <MoreVertical16Regular />
                                        </button>
                                    </MenuTrigger>
                                    <MenuPopover>
                                        <MenuList>
                                            <MenuItem
                                                icon={<Rename16Regular />}
                                                onClick={() => {
                                                    setRenamingId(chat.id);
                                                    setRenameValue(title);
                                                }}>
                                                Rename
                                            </MenuItem>
                                            <MenuItem icon={<Delete16Regular />} onClick={() => handleDeleteChat(chat.id)}>
                                                Delete
                                            </MenuItem>
                                        </MenuList>
                                    </MenuPopover>
                                </Menu>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className={styles.page}>
            <Pageheader title="LENS - Journal Analysis" />
            <header className={styles.header}>
                <div className={styles.headerLeft}>
                    <button type="button" className={styles.backBtn} onClick={() => navigate('/journal')}>
                        <ArrowLeft16Regular />
                        Back to Journal
                    </button>
                    <h1 className={styles.title}>Journal Analysis</h1>
                </div>
                <span className={styles.privateBadge}>
                    <LockClosed16Regular />
                    Private workspace
                </span>
            </header>

            <div className={styles.body}>
                <aside className={styles.sidebar}>
                    <div className={styles.sidebarTop}>
                        <button type="button" className={styles.newAnalysisBtn} onClick={handleNewAnalysis}>
                            <Add16Regular />
                            New analysis
                        </button>
                        <div className={styles.searchWrap}>
                            <Search16Regular color="#989bad" />
                            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search analyses" />
                        </div>
                    </div>
                    <div className={styles.sidebarList}>
                        {listLoading ? (
                            <div style={{ padding: 24, display: 'flex', justifyContent: 'center' }}>
                                <Spinner size="small" />
                            </div>
                        ) : (
                            <>
                                {renderChatGroup('Today', grouped.today)}
                                {renderChatGroup('Previous 7 Days', grouped.week)}
                                {renderChatGroup('Older', grouped.older)}
                                {!filteredChats.length ? (
                                    <div className={styles.emptyState} style={{ padding: 24 }}>
                                        <p>No analyses yet. Start a new one.</p>
                                    </div>
                                ) : null}
                            </>
                        )}
                    </div>
                </aside>

                <main className={styles.main}>
                    <div className={styles.mainTop}>
                        <div className={styles.contextPanel}>
                            <div className={styles.contextLeft}>
                                <div className={styles.contextLabelWrap}>
                                    <Info16Regular color="#989bad" />
                                    <span className={styles.contextLabel}>Context:</span>
                                </div>
                                {hasContext ? (
                                    <>
                                        <span className={styles.contextSummary}>
                                            {contextEntryCount} {contextEntryCount === 1 ? 'entry' : 'entries'} across {contextSectionCount}{' '}
                                            {contextSectionCount === 1 ? 'section' : 'sections'}
                                        </span>
                                        <div className={styles.chipsGroup}>
                                            {visibleSectionChips.map(name => (
                                                <span key={name} className={styles.chip}>
                                                    {name}
                                                </span>
                                            ))}
                                            {hiddenSectionCount > 0 ? <span className={styles.chipMore}>+{hiddenSectionCount} more</span> : null}
                                        </div>
                                    </>
                                ) : (
                                    <span className={styles.contextSummary}>No sections selected</span>
                                )}
                            </div>
                            <button type="button" className={styles.changeContextBtn} onClick={() => setContextOpen(true)}>
                                {hasContext ? 'Change context' : 'Select context'}
                            </button>
                        </div>

                        <div className={styles.toolbar}>
                            <Menu>
                                <MenuTrigger disableButtonEnhancement>
                                    <button type="button" className={styles.suggestedAnalysesBtn}>
                                        Suggested analyses
                                    </button>
                                </MenuTrigger>
                                <MenuPopover>
                                    <MenuList>
                                        {SUGGESTED_ANALYSES.map(item => (
                                            <MenuItem key={item.id} onClick={() => setDraft(item.prompt)}>
                                                {item.title}
                                            </MenuItem>
                                        ))}
                                    </MenuList>
                                </MenuPopover>
                            </Menu>
                            <button
                                type="button"
                                className={styles.savedPromptsBtn}
                                onClick={() => setSavedPromptsOpen(true)}>
                                Saved prompts
                            </button>
                        </div>
                    </div>

                    {error ? <div className={styles.errorBanner}>{error}</div> : null}

                    <div className={styles.thread} ref={threadRef}>
                        {messagesLoading ? (
                            <div style={{ margin: 'auto' }}>
                                <Spinner />
                            </div>
                        ) : null}
                        {!messagesLoading && !messages.length ? (
                            <div className={styles.emptyState}>
                                <h2>Analyse your journal</h2>
                                <p>Select context from your journal sections and entries, then ask a question or pick a suggested analysis.</p>
                            </div>
                        ) : null}
                        {messages.map(msg => {
                            const isUser = msg.role === 'user';
                            return (
                                <div key={msg.id} className={styles.messageBlock}>
                                    <div className={isUser ? styles.avatarUser : styles.avatarAi}>{isUser ? userInitials : <Sparkle16Filled color="#ffffff" />}</div>
                                    <div className={styles.messageContent}>
                                        <div className={styles.messageMeta}>
                                            <span className={styles.messageName}>{isUser ? userDisplayName : 'AI Analysis'}</span>
                                            <span className={styles.messageTime}>{formatMessageTime(msg.created_at)}</span>
                                        </div>
                                        {isUser ? (
                                            <div className={styles.userBubble}>{msg.content}</div>
                                        ) : (
                                            <>
                                                <div className={styles.markdown}>
                                                    <ReactMarkdown>{msg.content || ''}</ReactMarkdown>
                                                </div>
                                                <div className={styles.messageActions}>
                                                    <button type="button" className={styles.actionChip} onClick={() => handleCopy(msg.content)}>
                                                        <Copy16Regular /> Copy
                                                    </button>
                                                    <button type="button" className={styles.actionChip} onClick={() => handleSaveAsJournal(msg.content)}>
                                                        <DocumentAdd16Regular /> Save as Journal Entry
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={styles.actionChip}
                                                        onClick={() => {
                                                            setSidebarContent(markdownToJournalHtml(msg.content || '') || msg.content);
                                                            setShowObsSidebar(true);
                                                        }}>
                                                        <Eye16Regular /> Create Observation
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={styles.actionChip}
                                                        onClick={() => {
                                                            setSidebarContent(msg.content || '');
                                                            setShowTaskSidebar(true);
                                                        }}>
                                                        <CheckboxChecked16Regular /> Create Tasks
                                                    </button>
                                                    <button type="button" className={styles.actionChip} onClick={() => handleExportPdf(msg.content)}>
                                                        <ArrowDownload16Regular /> Export PDF
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        {sending ? (
                            <div className={styles.thinkingLoaderWrap} role="status" aria-live="polite">
                                <div className={styles.thinkingLoader}>
                                    <ThinkingOrb
                                        state="composing"
                                        size={64}
                                        speed={2}
                                        theme="light"
                                        aria-label="Thinking"
                                    />
                                    <span className={styles.thinkingLoaderLabel}>Thinking…</span>
                                </div>
                            </div>
                        ) : null}
                    </div>

                    <div className={styles.composerWrap}>
                        <div className={styles.composerInner}>
                            {hasContext ? (
                                <div className={styles.composerContext}>
                                    <span>
                                        Using {contextEntryCount} {contextEntryCount === 1 ? 'entry' : 'entries'} across {contextSectionCount}{' '}
                                        {contextSectionCount === 1 ? 'section' : 'sections'}
                                    </span>
                                    <button type="button" className={styles.composerChangeLink} onClick={() => setContextOpen(true)}>
                                        Change
                                    </button>
                                </div>
                            ) : (
                                <div className={styles.composerContext}>
                                    <span>No journal context selected</span>
                                    <button type="button" className={styles.composerChangeLink} onClick={() => setContextOpen(true)}>
                                        Select
                                    </button>
                                </div>
                            )}

                            <div className={styles.composer}>
                                <button
                                    type="button"
                                    className={`${styles.iconAction} ${isDraftSavedPrompt ? styles.iconActionSaved : ''}`}
                                    title={isDraftSavedPrompt ? 'Prompt saved' : 'Save prompt'}
                                    onClick={handleSavePrompt}>
                                    {isDraftSavedPrompt ? <Bookmark16Filled color="var(--Brand-Secondary, #c89741)" /> : <Bookmark16Regular />}
                                </button>
                                <textarea
                                    value={draft}
                                    rows={1}
                                    onChange={e => {
                                        const value = e.target.value;
                                        setDraft(value);
                                        if (!isListening) {
                                            committedDraftRef.current = value;
                                        }
                                    }}
                                    placeholder="Ask a follow-up about the selected notes…"
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleSend();
                                        }
                                    }}
                                />
                                <div className={styles.composerRight}>
                                    <div className={styles.micWrap}>
                                        {isListening ? (
                                            <RecordingPopover active={isListening} onStop={handleToggleMic} />
                                        ) : null}
                                        <button
                                            type="button"
                                            className={`${styles.iconAction} ${isListening ? styles.micActive : ''}`}
                                            title={isListening ? 'Stop dictation' : 'Start voice dictation'}
                                            aria-label={isListening ? 'Stop dictation' : 'Start voice dictation'}
                                            aria-pressed={isListening}
                                            onClick={handleToggleMic}>
                                            {isListening ? <MicOff20Regular /> : <Mic20Regular />}
                                        </button>
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.sendBtn}
                                        disabled={!draft.trim() || sending}
                                        onClick={() => handleSend()}
                                        aria-label="Send">
                                        <Send16Regular />
                                    </button>
                                </div>
                            </div>
                            <p className={styles.composerHint}>Press Enter to send · Shift + Enter for new line</p>
                        </div>
                    </div>
                </main>
            </div>

            <ContextModal
                open={contextOpen}
                sections={notesState}
                projectUsersAll={projectUsersAll}
                initialSelectedIds={activeChat?.context_note_ids || []}
                onClose={() => setContextOpen(false)}
                onConfirm={handleConfirmContext}
            />

            <AskAiSavedPromptsModal
                open={savedPromptsOpen}
                onClose={() => setSavedPromptsOpen(false)}
                onUsePrompt={text => setDraft(text)}
            />

            <ObservationFormSidebar
                show={showObsSidebar}
                setShow={show => {
                    setShowObsSidebar(show);
                    if (!show) setSidebarContent('');
                }}
                defaultObservation={() => sidebarContent || ''}
            />
            <TaskFormSidebar
                show={showTaskSidebar}
                setShow={show => {
                    setShowTaskSidebar(show);
                    if (!show) setSidebarContent('');
                }}
                defaultTask={() => htmlToPlainText(sidebarContent || '')}
            />
        </div>
    );
};

export default JournalAnalysis;
